// https://github.com/Hideki-Kobayashi-Eclair/accordion-menu
class Accordion {
  static #OPENED = "opened";
  static #OPENING = "opening";
  static #CLOSED = "closed";
  static #CLOSING = "closing";

  #details;
  #summary;
  #content;
  #duration = 300;
  #timingFunction = "ease";
  #delay = 0;
  #_status;

  constructor(options) {
    if (!options.target) throw new Error("'target' must need Element.");

    this.#details = options.target;
    this.#summary = this.#details.querySelector("summary");
    this.#content = this.#details.querySelector("summary + *");
    if (!this.#summary || !this.#content) return;

    this.#duration = options.duration ?? this.#duration;
    this.#timingFunction = options.timingFunction ?? this.#timingFunction;
    this.#delay = options.delay ?? this.#delay;

    this.#details.style.overflow = "hidden";
    this.#details.dataset.accordionInit = "";

    this.#details.style.transition = `height ${this.#duration}ms ${
      this.#timingFunction
    } ${this.#delay}ms`;

    this.#status = options.isOpenOnDefault
      ? Accordion.#OPENED
      : Accordion.#CLOSED;

    this.#summary.addEventListener("click", (event) => this.#toggle(event));

    this.#details.addEventListener("transitionend", (event) => {
      if (event.propertyName === "height") {
        this.#onTransitionEnd();
      }
    });
  }

  get #status() {
    return this.#_status;
  }

  set #status(status) {
    this.#_status = status;

    switch (status) {
      case Accordion.#OPENED:
        this.#details.setAttribute("open", "");
        this.#summary.setAttribute("aria-expanded", "true");
        this.#details.style.height = "auto";
        break;

      case Accordion.#OPENING:
        this.#details.setAttribute("open", "");
        this.#summary.setAttribute("aria-expanded", "true");
        this.#details.style.height = this.#openingHeight;
        break;

      case Accordion.#CLOSED:
        this.#details.removeAttribute("open");
        this.#summary.setAttribute("aria-expanded", "false");
        this.#details.style.height = this.#closingHeight;
        break;

      case Accordion.#CLOSING:
        this.#details.setAttribute("open", "");
        this.#summary.setAttribute("aria-expanded", "false");
        this.#details.style.height = this.#closingHeight;
        break;
    }
  }

  get #openingHeight() {
    return (
      this.#summary.getBoundingClientRect().height +
      this.#content.getBoundingClientRect().height +
      this.#getVerticalBorderWidth() +
      "px"
    );
  }

  get #closingHeight() {
    return (
      this.#summary.getBoundingClientRect().height +
      this.#getVerticalBorderWidth() +
      "px"
    );
  }

  #getVerticalBorderWidth() {
    const computedStyle = getComputedStyle(this.#details);
    const borderTopWidth = parseInt(
      computedStyle.getPropertyValue("border-top-width")
    );
    const borderBottomWidth = parseInt(
      computedStyle.getPropertyValue("border-bottom-width")
    );
    return borderTopWidth + borderBottomWidth;
  }

  #forceReflow() {
    this.#details.offsetHeight;
  }

  #toggle(event) {
    event.preventDefault();

    switch (this.#status) {
      case Accordion.#OPENED:
        this.#details.style.height = this.#openingHeight;
        this.#forceReflow();
        this.#status = Accordion.#CLOSING;
        break;

      case Accordion.#OPENING:
        this.#status = Accordion.#CLOSING;
        break;

      case Accordion.#CLOSED:
        this.#details.style.height = this.#closingHeight;
        this.#forceReflow();
        this.#status = Accordion.#OPENING;
        break;

      case Accordion.#CLOSING:
        this.#status = Accordion.#OPENING;
        break;
    }
  }

  #onTransitionEnd() {
    if (this.#status === Accordion.#CLOSING) {
      this.#status = Accordion.#CLOSED;
      return;
    }

    if (this.#status === Accordion.#OPENING) {
      this.#status = Accordion.#OPENED;
      return;
    }
  }
}

export const accordion = () => {
  document.querySelectorAll(".js-accordion").forEach((element) => {
    new Accordion({
      target: element,
      duration: 700,
      timingFunction: "ease",
    });
  });
};
